import { useState, useEffect, useMemo, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { TimeField } from "@/components/TimeField";
import { DurationPicker } from "@/components/DurationPicker";
import { findOwnerDuplicates } from "@/components/OwnerFields";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { todayISO, fmtEUR } from "@/lib/format";
import { Switch } from "@/components/ui/switch";
import { ChevronDown, Search, AlertTriangle, UserCheck, UserPlus, Coins } from "lucide-react";

const emptyContact = { nome: "", cognome: "", telefono: "", email: "", dog_name: "", nota: "" };

export const QuickAppointmentDialog = ({ open, onOpenChange, onSaved }) => {
  const [owners, setOwners] = useState([]);
  const [dogs, setDogs] = useState([]);
  const [types, setTypes] = useState([]);
  const [methods, setMethods] = useState(["Contanti", "Carta/POS", "Bonifico", "Altro"]);
  const [subject, setSubject] = useState("existing"); // existing | new
  const [ownerId, setOwnerId] = useState("");
  const [dogId, setDogId] = useState("");
  const [ownerOpen, setOwnerOpen] = useState(false);
  const [ownerQuery, setOwnerQuery] = useState("");
  const [contact, setContact] = useState(emptyContact);
  const [appt, setAppt] = useState({});
  const [payMode, setPayMode] = useState("none"); // none | full | acconto
  const [pay, setPay] = useState({ amount: "", method: "Contanti", date: "", notes: "" });
  const [saving, setSaving] = useState(false);
  const [reqKey, setReqKey] = useState("");
  const [apptFree, setApptFree] = useState(false);
  const [qaPrev, setQaPrev] = useState(null);
  const savingRef = useRef(false);

  useEffect(() => {
    if (!open) return;
    Promise.all([api.get("/owners"), api.get("/dogs"), api.get("/service-types"), api.get("/settings")])
      .then(([o, d, t, s]) => {
        setOwners(o.data); setDogs(d.data);
        const filtered = t.data.filter((x) => x.active && !x.is_package_type);
        setTypes(filtered);
        // Hydrate the initial value/duration from the preselected service's catalogue price
        // (same source as manual onType). Generic: reads whatever service_type is the default.
        setAppt((a) => {
          const def = filtered.find((x) => x.name === a.service_type) || filtered[0];
          return def ? { ...a, service_type: def.name, value: def.default_price || 0, duration_minutes: def.default_duration_minutes || a.duration_minutes } : a;
        });
        const pm = (s.data.payment_methods || []).map((m) => (typeof m === "string" ? { name: m, active: true } : m))
          .filter((m) => m.active !== false).map((m) => m.name);
        if (pm.length) setMethods(pm);
      }).catch(() => {});
    setSubject("existing"); setOwnerId(""); setDogId(""); setOwnerOpen(false); setOwnerQuery("");
    setContact(emptyContact);
    setAppt({ service_type: "Consulenza singola", date: todayISO(), start_time: "", duration_minutes: 60, location: "", notes: "", value: 0 });
    setPayMode("none"); setPay({ amount: "", method: "Contanti", date: "", notes: "" });
    setApptFree(false); setQaPrev(null);
    setReqKey((typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`);
  }, [open]);

  const dogsByOwner = (oid) => dogs.filter((d) => d.owner_id === oid).map((d) => d.nome);
  const ownerLabel = (o) => {
    const nm = `${o.nome} ${o.cognome || ""}`.trim();
    const ds = dogsByOwner(o.id);
    return ds.length ? `${nm} (${ds.join(", ")})` : nm;
  };
  const filteredOwners = owners.filter((o) => {
    const q = ownerQuery.trim().toLowerCase();
    if (!q) return true;
    const nm = `${o.nome} ${o.cognome || ""}`.toLowerCase();
    return nm.includes(q) || dogsByOwner(o.id).some((n) => n.toLowerCase().includes(q)) || (o.telefono || "").includes(q);
  });
  const selectedOwner = owners.find((o) => o.id === ownerId);
  const ownerDogs = dogs.filter((d) => d.owner_id === ownerId);

  const dups = useMemo(() => {
    if (subject !== "new") return [];
    if (!contact.nome.trim() && !contact.email.trim() && !contact.telefono.trim()) return [];
    return findOwnerDuplicates(owners, contact);
  }, [subject, contact, owners]);

  const val = Number(appt.value || 0);
  const selType = types.find((x) => x.name === appt.service_type);
  const consumesCredits = !!(selType && selType.default_credits > 0);

  // Reuse the canonical credit/FIFO preview engine (same endpoint as the normal prestation flow).
  useEffect(() => {
    if (!open || subject !== "existing" || !dogId || !consumesCredits || apptFree) { setQaPrev(null); return; }
    api.post("/sessions/preview-credits", null, { params: { dog_id: dogId, duration_minutes: appt.duration_minutes, credits: 0 } })
      .then((r) => setQaPrev(r.data)).catch(() => setQaPrev(null));
  }, [open, subject, dogId, consumesCredits, apptFree, appt.duration_minutes]);

  const onType = (v) => {
    const t = types.find((x) => x.name === v);
    setAppt((a) => ({
      ...a, service_type: v,
      value: t ? t.default_price : a.value,
      duration_minutes: t && t.default_duration_minutes ? t.default_duration_minutes : a.duration_minutes,
    }));
  };

  const applyExisting = (m) => {
    setSubject("existing"); setOwnerId(m.id); setDogId(""); setOwnerQuery(""); setOwnerOpen(false);
  };

  const toggleFree = (v) => {
    setApptFree(v);
    if (v) setAppt((a) => ({ ...a, value: 0 }));
  };

  const submit = async () => {
    // Validation (system boundary)
    if (subject === "new") {
      if (!contact.nome.trim()) return toast.error("Il nome del referente è obbligatorio.");
      if (!contact.dog_name.trim()) return toast.error("Inserisci il nome del cane per creare l'appuntamento.");
    } else {
      if (!ownerId) return toast.error("Seleziona un cliente esistente.");
      if (!dogId) return toast.error("Seleziona un cane.");
    }
    if (payMode !== "none" && val > 0) {
      const amount = payMode === "full" ? val : Number(pay.amount || 0);
      if (!amount || amount <= 0) return toast.error("Inserisci un importo di acconto valido.");
      if (amount > val + 0.01) return toast.error("Il pagamento non può superare il valore del servizio.");
    }
    if (savingRef.current) return;
    savingRef.current = true;
    setSaving(true);
    try {
      let oid = ownerId, did = dogId;
      if (subject === "new") {
        const r = await api.post("/contacts/quick", {
          nome: contact.nome, cognome: contact.cognome, telefono: contact.telefono,
          email: contact.email, dog_name: contact.dog_name, nota: contact.nota,
        });
        oid = r.data.owner.id;
        did = r.data.dog?.id;
      }
      const sres = await api.post("/sessions", {
        owner_id: oid, dog_ids: [did], service_type: appt.service_type,
        date: appt.date, start_time: appt.start_time || "", duration_minutes: Number(appt.duration_minutes),
        status: "Pianificata", location: appt.location || "", notes: appt.notes || "",
        value: val, package_linked: consumesCredits && !apptFree, credits: 0, is_free: apptFree, request_key: reqKey,
      });
      const sessionId = sres.data.id;

      if (payMode !== "none" && val > 0) {
        const amount = payMode === "full" ? val : Number(pay.amount || 0);
        try {
          await api.post("/payments", {
            date: pay.date || appt.date, owner_id: oid, dog_id: did, session_id: sessionId,
            amount, method: pay.method, type: "Pagamento", destination_type: "SESSION", notes: pay.notes || "",
          });
        } catch (e) {
          toast.error("Appuntamento creato, ma il pagamento NON è stato registrato: " + apiErr(e));
          onSaved?.(); onOpenChange(false); return;
        }
      }
      toast.success("Appuntamento rapido creato");
      onSaved?.(); onOpenChange(false);
    } catch (e) {
      setReqKey((typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`);
      toast.error(apiErr(e));
    } finally {
      setSaving(false);
      savingRef.current = false;
    }
  };

  const setC = (k) => (e) => setContact((c) => ({ ...c, [k]: e.target.value }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto wg-scroll">
        <DialogHeader><DialogTitle className="font-head">Appuntamento rapido</DialogTitle></DialogHeader>
        <div className="space-y-4">
          {/* STEP 1 — Subject */}
          <div className="grid grid-cols-2 gap-2">
            <button type="button" data-testid="qa-subject-existing" onClick={() => setSubject("existing")}
              className={`flex items-center justify-center gap-2 rounded-xl border p-3 text-sm font-semibold transition-colors ${subject === "existing" ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border"}`}>
              <UserCheck className="h-4 w-4" />Anagrafica esistente
            </button>
            <button type="button" data-testid="qa-subject-new" onClick={() => setSubject("new")}
              className={`flex items-center justify-center gap-2 rounded-xl border p-3 text-sm font-semibold transition-colors ${subject === "new" ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border"}`}>
              <UserPlus className="h-4 w-4" />Nuovo contatto
            </button>
          </div>

          {subject === "existing" ? (
            <div className="space-y-3">
              <div className="space-y-1">
                <Label>Cliente *</Label>
                <div className="relative">
                  <button type="button" data-testid="qa-owner" onClick={() => setOwnerOpen((v) => !v)}
                    className="flex w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm">
                    <span className={selectedOwner ? "" : "text-muted-foreground"}>{selectedOwner ? ownerLabel(selectedOwner) : "Cerca per cliente o cane…"}</span>
                    <ChevronDown className="h-4 w-4 opacity-60 shrink-0" />
                  </button>
                  {ownerOpen && (
                    <div className="absolute z-50 mt-1 w-full rounded-md border border-border bg-popover shadow-md">
                      <div className="flex items-center gap-2 border-b border-border px-2 py-1.5">
                        <Search className="h-4 w-4 opacity-60" />
                        <input autoFocus data-testid="qa-owner-search" value={ownerQuery} onChange={(e) => setOwnerQuery(e.target.value)}
                          placeholder="Cerca cliente o cane…" className="w-full bg-transparent text-sm outline-none" />
                      </div>
                      <div className="max-h-56 overflow-y-auto py-1">
                        {filteredOwners.length === 0 && <div className="px-3 py-2 text-sm text-muted-foreground">Nessun risultato.</div>}
                        {filteredOwners.map((o) => (
                          <button key={o.id} type="button" data-testid={`qa-owner-opt-${o.id}`}
                            onClick={() => { setOwnerId(o.id); setDogId(""); setOwnerOpen(false); setOwnerQuery(""); }}
                            className={`block w-full px-3 py-2 text-left text-sm hover:bg-accent ${o.id === ownerId ? "bg-accent" : ""}`}>
                            {ownerLabel(o)}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              {ownerId && (
                <div className="space-y-1">
                  <Label>Cane *</Label>
                  <div className="flex flex-wrap gap-2" data-testid="qa-dogs">
                    {ownerDogs.map((d) => (
                      <button key={d.id} type="button" data-testid={`qa-dog-${d.id}`} onClick={() => setDogId(d.id)}
                        className={`rounded-full border px-3 py-1.5 text-sm font-semibold transition-colors ${dogId === d.id ? "bg-primary text-primary-foreground border-primary" : "bg-secondary border-border"}`}>
                        {d.nome}
                      </button>
                    ))}
                    {ownerDogs.length === 0 && <span className="text-sm text-muted-foreground">Nessun cane. Aggiungilo dal profilo cliente.</span>}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-3 rounded-xl border border-border p-3 bg-secondary/40" data-testid="qa-new-contact">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1"><Label>Referente / nome *</Label><Input data-testid="qa-nome" value={contact.nome} onChange={setC("nome")} /></div>
                <div className="space-y-1"><Label>Cognome</Label><Input data-testid="qa-cognome" value={contact.cognome} onChange={setC("cognome")} /></div>
                <div className="space-y-1"><Label>Telefono</Label><Input data-testid="qa-telefono" value={contact.telefono} onChange={setC("telefono")} /></div>
                <div className="space-y-1"><Label>Email</Label><Input data-testid="qa-email" value={contact.email} onChange={setC("email")} /></div>
                <div className="space-y-1"><Label>Nome cane *</Label><Input data-testid="qa-dogname" value={contact.dog_name} onChange={setC("dog_name")} /></div>
                <div className="space-y-1"><Label>Nota rapida</Label><Input data-testid="qa-nota" value={contact.nota} onChange={setC("nota")} /></div>
              </div>
              {dups.length > 0 && (
                <div className="rounded-lg border border-[#FFE082] bg-[#FFF8E1] p-2.5 text-sm" data-testid="qa-dup-warning">
                  <div className="flex items-center gap-1.5 font-semibold text-[#8D6E63]"><AlertTriangle className="h-4 w-4" />Esiste già un'anagrafica che potrebbe corrispondere a questo contatto.</div>
                  <div className="mt-1.5 space-y-1">
                    {dups.map((m) => (
                      <button key={m.id} type="button" data-testid={`qa-dup-use-${m.id}`} onClick={() => applyExisting(m)}
                        className="w-full text-left rounded-md border border-border bg-card p-2 hover:border-primary">
                        <span className="font-semibold">Usa esistente: {m.nome} {m.cognome}</span>
                        <span className="block text-xs text-muted-foreground">{[m.telefono, m.email, (dogsByOwner(m.id).join(", "))].filter(Boolean).join(" · ") || "—"}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* STEP 2 — Appointment */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1 col-span-2"><Label>Servizio</Label>
              <Select value={appt.service_type} onValueChange={onType}>
                <SelectTrigger data-testid="qa-service"><SelectValue /></SelectTrigger>
                <SelectContent>{types.map((t) => <SelectItem key={t.id} value={t.name}>{t.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1"><Label>Data</Label>
              <Input type="date" data-testid="qa-date" value={appt.date} onChange={(e) => setAppt((a) => ({ ...a, date: e.target.value }))} /></div>
            <div className="space-y-1"><Label>Ora inizio</Label>
              <TimeField value={appt.start_time} onChange={(v) => setAppt((a) => ({ ...a, start_time: v }))} testid="qa-time" /></div>
            <div className="space-y-1"><Label>Durata</Label>
              <DurationPicker value={appt.duration_minutes} onChange={(m) => setAppt((a) => ({ ...a, duration_minutes: m }))} testid="qa-duration" /></div>
            <div className="space-y-1"><Label>Valore (€)</Label>
              <Input type="number" data-testid="qa-value" value={appt.value} disabled={apptFree} onFocus={(e) => e.target.select()} onChange={(e) => setAppt((a) => ({ ...a, value: e.target.value }))} /></div>
            <div className="space-y-1 col-span-2"><Label>Luogo</Label>
              <Input data-testid="qa-location" value={appt.location} onChange={(e) => setAppt((a) => ({ ...a, location: e.target.value }))} /></div>
            <div className="space-y-1 col-span-2"><Label>Nota</Label>
              <Textarea data-testid="qa-notes" value={appt.notes} onChange={(e) => setAppt((a) => ({ ...a, notes: e.target.value }))} /></div>
          </div>

          {/* Prestazione gratuita — valido per qualsiasi servizio (semantica canonica: is_free) */}
          <div className="flex items-center justify-between rounded-xl border border-border p-3 bg-secondary/40">
            <div>
              <Label className="cursor-pointer">Prestazione gratuita / non scalare crediti</Label>
              <p className="text-xs text-muted-foreground">Valore €0, nessun credito consumato. Valido per qualsiasi servizio.</p>
            </div>
            <Switch data-testid="qa-free" checked={apptFree} onCheckedChange={toggleFree} />
          </div>

          {/* Package credit impact — reuses the canonical engine */}
          {subject === "existing" && dogId && consumesCredits && !apptFree && (
            <div className="rounded-xl border border-[#B7D3C4] bg-[#E6EFEA] p-3 space-y-2" data-testid="qa-credit-preview">
              <div className="flex items-center gap-2 text-[#234735] font-semibold text-sm"><Coins className="h-4 w-4" />Impatto crediti pacchetto</div>
              {qaPrev?.active_package ? (
                <>
                  <div className="flex justify-between text-sm">
                    <span>Pacchetto aperto {qaPrev.active_package.opening_date ? qaPrev.active_package.opening_date.split("-").reverse().join("/") : "—"}: {qaPrev.active_package.used_credits}/{qaPrev.active_package.total_credits} utilizzate</span>
                    <span className="font-mono">{qaPrev.active_package.remaining_credits} residui</span>
                  </div>
                  <p className="text-xs text-[#4B6B58]" data-testid="qa-fifo-note">Pacchetto selezionato automaticamente (FIFO). I crediti verranno scalati quando la prestazione sarà segnata come Svolta.</p>
                </>
              ) : (
                <p className="text-sm text-[#8D6E63]" data-testid="qa-no-package">Nessun pacchetto attivo per questo cane. La prestazione verrà creata senza consumo crediti.</p>
              )}
            </div>
          )}

          {/* Payment */}
          <div className="rounded-xl border border-border p-3 bg-secondary/40 space-y-3">
            <Label>Pagamento</Label>
            {val <= 0 ? (
              <p className="text-sm text-muted-foreground" data-testid="qa-pay-zero">Servizio a €0 — nessun pagamento da registrare.</p>
            ) : (
              <>
                <div className="flex flex-wrap gap-2">
                  {[["none", "Nessun pagamento"], ["full", "Pagato interamente"], ["acconto", "Acconto"]].map(([v, l]) => (
                    <button key={v} type="button" data-testid={`qa-pay-${v}`} onClick={() => setPayMode(v)}
                      className={`rounded-full border px-3 py-1.5 text-sm font-semibold transition-colors ${payMode === v ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border"}`}>{l}</button>
                  ))}
                </div>
                {payMode !== "none" && (
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1"><Label>Importo (€)</Label>
                      <Input type="number" data-testid="qa-pay-amount"
                        value={payMode === "full" ? val : pay.amount}
                        disabled={payMode === "full"}
                        onFocus={(e) => e.target.select()}
                        onChange={(e) => setPay((p) => ({ ...p, amount: e.target.value }))} />
                      <p className="text-xs text-muted-foreground">Valore servizio: {fmtEUR(val)}</p></div>
                    <div className="space-y-1"><Label>Metodo</Label>
                      <Select value={pay.method} onValueChange={(v) => setPay((p) => ({ ...p, method: v }))}>
                        <SelectTrigger data-testid="qa-pay-method"><SelectValue /></SelectTrigger>
                        <SelectContent>{methods.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                      </Select></div>
                    <div className="space-y-1"><Label>Data pagamento</Label>
                      <Input type="date" data-testid="qa-pay-date" value={pay.date || appt.date} onChange={(e) => setPay((p) => ({ ...p, date: e.target.value }))} /></div>
                    <div className="space-y-1"><Label>Nota pagamento</Label>
                      <Input data-testid="qa-pay-notes" value={pay.notes} onChange={(e) => setPay((p) => ({ ...p, notes: e.target.value }))} /></div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annulla</Button>
          <Button data-testid="qa-save" onClick={submit} disabled={saving}>{saving ? "Salvataggio…" : "Crea appuntamento"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
